import { useCallback, useEffect, useRef, useState } from 'react';
import { isHistoricalViewerYear } from '../../../shared/utils/reportingYear';

export default function useEmissionScreen({
  request, normalize, createEmpty, historicalRequest, normalizeHistorical,
}) {
  const initialYearRef = useRef(createEmpty().meta?.year);
  const [requestedYear, setRequestedYear] = useState(undefined);
  const [data, setData] = useState(() => createEmpty());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sequenceRef = useRef(0);
  const controllerRef = useRef(null);

  const load = useCallback(async (targetYear, force = false) => {
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setData(createEmpty(targetYear));
    setLoading(true);
    setError(null);
    try {
      const isHistorical = isHistoricalViewerYear(targetYear);
      const response = await (isHistorical && historicalRequest
        ? historicalRequest(targetYear, { signal: controller.signal })
        : request(targetYear, {
        signal: controller.signal,
        force,
        }));
      if (controller.signal.aborted || sequenceRef.current !== sequence) return;
      const normalized = isHistorical && normalizeHistorical
        ? normalizeHistorical(response, targetYear)
        : normalize(response);
      setData(normalized);
    } catch (loadError) {
      if (controller.signal.aborted || sequenceRef.current !== sequence) return;
      setError(loadError);
    } finally {
      if (!controller.signal.aborted && sequenceRef.current === sequence) {
        setLoading(false);
      }
    }
  }, [createEmpty, historicalRequest, normalize, normalizeHistorical, request]);

  useEffect(() => {
    load(requestedYear ?? initialYearRef.current);
    return () => controllerRef.current?.abort();
  }, [load, requestedYear]);

  const setYear = useCallback((nextYear) => {
    const normalizedYear = Number(nextYear);
    setRequestedYear(normalizedYear);
    setData(createEmpty(normalizedYear));
    setError(null);
  }, [createEmpty]);

  const reload = useCallback(() => load(requestedYear, true), [load, requestedYear]);

  return {
    data,
    year: requestedYear ?? data.meta.year,
    years: data.meta.years,
    setYear,
    loading,
    error,
    reload,
  };
}
